import { ConfigurationsServeByDeviceAction, KalturaResponseType } from '@kaltura-ott/ott-client-types/api/types';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { KuxMenuItem, TypeOfApplicationLink } from '../interfaces';
import { PREDEFINED_REFERENCES } from '../lib/utils/constants';

import { normalizingProviderData, prepareProviders, Provider, ProviderSettings } from './dms.helper';
import { KuxError } from './errors';
import { kalturaClient } from './kaltura-clent';
import { generateMenuItem } from './menu.helper';
import { arrayToMap } from './utils/common';

export type { Provider, ProviderSettings };

export enum ConnectivityType {
  iptv = 'IPTV',
  ott = 'OTT',
}

export enum FileFormatType {
  multicast = 'multicast',
  mpegdash = 'mpegdash',
}
/**
 * FilesFormat
 */
export enum FilesFormat {
  live = 'LIVE',
  trailer = 'TRAILER',
  vod = 'VOD',
  welcomePopup = 'WELCOME_POPUP',
  liveHEVC = 'LIVE_HEVC',
  vodHEVC = 'VOD_HEVC',
}

export interface PlayerLanguageCode {
  key: string;
  code: string;
  nativeName: string;
}

export interface AppLanguageCodes {
  [lang: string]: string;
}

export interface LocalVqSettings {
  [typeName: string]: { top_range_bitrate: number };
}

export type LocalVqSettingItem = [string, { top_range_bitrate: number }];

/**
 * MediaType
 */
export enum MediaType {
  Episode = 'Episode',
  Movie = 'Movie',
  Series = 'Series',
  Linear = 'Linear',
  Program = 'Program',
  Season = 'Season',
  Package = 'Package',
  Person = 'Person',
  Logo = 'Logo',
  in_application_link_asset = 'in_application_link_asset',
}
export enum PINType {
  SN = 'SN',
  IP = 'IP',
  MAC = 'MAC',
  UDID = 'UDID',
  WEBSLID = 'WEBSLID',
}
export enum MACType {
  WIFI = 'WIFI',
  LAN = 'LAN',
}

export enum PinStructure {
  Secret = 'Secret',
  Pipe = 'Pipe',
  PIN = 'PIN',
}

export enum AdditionalMediaType {
  Recording = 'recording',
}

export enum AppInfoSerialType {
  MAC = 'MAC',
  UDID = 'UDID',
}

export type ExtendedMediaType = MediaType | AdditionalMediaType;

export enum MediaTypeId {
  Record = '1',
}

export enum BitrateLimitationMode {
  user = 'user',
  opco = 'opco',
}

export enum ExtractorMode {
  MODE_MULTI = 'MODE_MULTI',
  MODE_SINGLE_PMT = 'MODE_SINGLE_PMT',
  MODE_HLS = 'MODE_HLS',
}

export interface KuxRating {
  name: string;
  short: string;
  long: string;
}
export const DEFAULT_PLAYABLE_CATCHUP_BUFFER = '604800';
export const DEFAULT_SERVER_TIMEOUT = 10;
/**
 * DMS args
 */
export interface DmsRequestParameters {
  applicationName: string;
  clientVersion: string;
  platform: string;
  tag: string;
  udid: string;
  partnerId: number;
}

export interface DmsYouboraSettings {
  accountCode?: string;
}

export interface DmsPlayerSettings {
  scriptId?: string;
  uiConfId?: number;
  scriptUrl?: string;
  youbora?: DmsYouboraSettings;
  mediaEntryCacheConfig: {
    allowMediaEntryCaching: boolean;
    maxMediaEntryCacheSize: number;
    timeoutMs: number;
  };
  broadpeak?: {
    analyticsAddress: string;
    nanoCDNHost: string;
    broadpeakDomainNames: string;
  };
  warmupUrls?: string[];
  networkSettings?: {
    autoBuffer: boolean;
    preferredForwardBufferDuration: number;
    automaticallyWaitsToMinimizeStalling: boolean;
    preferredPeakBitRate: number;
  };
  ottAnalytics?: {
    timerInterval: number;
    experimentalLiveMediaHit: boolean;
  };
  lowLatencyConfig?: {
    minPlaybackSpeed: number;
    maxPlaybackSpeed: number;
  };
  loadControlBuffers?: {
    minPlayerBufferMs: number;
    maxPlayerBufferMs: number;
    minBufferAfterInteractionMs: number;
    minBufferAfterReBufferMs: number;
    backBufferDurationMs: number;
    retainBackBufferFromKeyframe: boolean;
    allowedVideoJoiningTimeMs: number;
    targetBufferBytes: number;
    prioritizeTimeOverSizeThresholds: boolean;
  };
  multicastLoadControlBuffers?: {
    minPlayerBufferMs: number;
    maxPlayerBufferMs: number;
    minBufferAfterInteractionMs: number;
    minBufferAfterReBufferMs: number;
    backBufferDurationMs: number;
    retainBackBufferFromKeyframe: boolean;
    allowedVideoJoiningTimeMs: number;
    targetBufferBytes: number;
    prioritizeTimeOverSizeThresholds: boolean;
  };
  multicastSettings?: {
    maxPacketSize: number;
    socketTimeoutMillis: number;
    extractorMode: ExtractorMode;
    firstSampleTimestampUs: number;
  };
  playerMulticastReloadTimeout?: number;
}

export type DmsAnalyticsPlugin = {
  [pluginName in string]: {
    config: {
      endpointUrl: string;
    };
  };
};

export interface PromotionScreenOptions {
  [key: string]: number;
}

export interface KuxDms {
  analytics: {
    externalKey: string;
    clientId?: string;
    CKEY?: string;
    plugins?: DmsAnalyticsPlugin[];
    ui?: {
      defaultThrottleTimeout?: number;
      epgThrottleTimeout?: number;
    };
    events?: {
      appStart?: boolean;
      lowFrequencyHeartbeat?: {
        period: number;
      };
      highFrequencyHeartbeat?: {
        period: number;
      };
      loginSuccesses?: boolean;
      playbackStart?: boolean;
      playbackResume?: boolean;
      spinner?: {
        period: number;
      };
      purchase?: boolean;
      homePageReady?: boolean;
      epgReady?: boolean;
      zapperPlaybackStart?: boolean;
      allowDeviceApplicationErrorEvents?: boolean;
      allowDevicePlaybackOverlayEvent?: boolean;
      allowDevicePlaybackActionEvent?: boolean;
      allowDevicePlaybackErrorEvent?: boolean;
      allowDeviceUserActionEvents?: boolean;
      allowDeviceActionEvents?: boolean;
      purchaseToPlay?: boolean;
    };
  };
  clientId?: string;
  CKEY?: string;
  apiVersion: string;
  pingServerUrl?: string;
  endpointUrl: string;
  cloudFrontUrl: string;
  graphQlUrl: string;
  serverTimeOut: number;
  slowConnectionSamplingPeriod?: number;
  bgVideo: number;
  bingeWatching: {
    enableNextEpisode: boolean;
    enableBingeWatchingUi: boolean;
    enableAutomaticNextEpisode: boolean;
    automaticNextEpisodeStartTime: number;
    nextEpisodeMaxCountdownTime: number;
    skipIntroMaxCountdownTime: number;
    skipRecapMaxCountdownTime: number;
  };
  bitrateLimitationManagement: {
    bitrateLimitSetBy: BitrateLimitationMode;
    userAllowedTo: boolean;
    bitrateLimit?: number;
    localVqSettings: LocalVqSettings;
  };
  multicastParams: {
    HEVCCapable?: boolean;
    WVCASCapable?: boolean;
    multicastEndpoint?: string;
    multicastIndicationKey?: string;
    multicastPauseResumeABRThreshold?: number;
  };
  companyLogoUrl?: string;
  consecutiveRequestFailuresLimit: number;
  filesFormat: { [type in FilesFormat]: string[] };
  forcedUpdateURLObject: { [key: string]: string };
  isForceUpdate?: boolean;
  sentry?: {
    dsn: string;
  };
  loadingAnimationUrl?: string;
  staticAssetsUrl?: string;
  loadingBgColor?: string;
  mediaTypes: { [type in MediaType]: string };
  mediaTypesValue: { [value: number]: MediaType };
  rootCategoryID: number; // deprecated need to remove
  e2eRootCategoryID?: number; // for automation tests
  upgradeWindowStartTime?: string;
  upgradeWindowEndTime?: string;
  tutorialVideoAssetId?: number;
  translationLinks: { [key: string]: string };
  suggestedRecalculationPeriod: number;
  suggestedCalculationByLastXItems: number;
  savedInterestItemsList?: number;
  chosenSearchList?: number;
  personalList?: number;
  showCastAndCrewInSearch: boolean;
  playerSettings?: DmsPlayerSettings;
  useCustomCustomerCookiesPopup?: boolean;
  o2Proxy?: string;
  wrongPinTriesLimit: number;
  wrongPinTimeout: number;
  playableCatchupBuffer: string;
  ratingMap: KuxRating[];
  ratingNameMap: Map<string, KuxRating>;
  rcuSettings?: rcuSettingsType;
  launchDarkly: {
    sdk?: string;
    mobile?: string;
  };
  deviceFamilyId?: number;
  appInfoSerialType?: AppInfoSerialType;
  temporaryParams: {
    endVoiceSearchTimeInSec: number;
    subscriptionDenialList: string[];
    ppvDenialList: string[];
    morePlanCollection?: string;
    filterLowerSubscription: boolean;
  };
  subscriptionIds?: {
    adults?: string[];
    fastForward?: string[];
  };
  adultsConfigurations?: {
    menuCategoryReferenceId?: string;
    categoryReferenceId?: string;
  };
  minFFEntitlementCheckIntervalSec: number;
  mosaicInfo: {
    isMosaicSupported: boolean;
    mosaicPlayerRandomSecondsTime: number;
  };
  silentLoginSettings: {
    loginTypeID?: string;
    pinType?: PINType[];
    macType?: MACType;
    structure?: PinStructure[];
    networkModeURL?: string;
  };
  prePaidUserId?: number;
  providers: ProviderSettings;
  epgTvTimelineInMinutes: number;
  GridCharacteristics: {
    GRID_TYPES: string[];
    GROUP_BY_VALUE: string;
  };
  stbMigrationUrl?: string;
  appLanguageCodes?: AppLanguageCodes;
  playerLanguageCodes?: PlayerLanguageCode[];
  isBGMigrationCheckNeeded?: boolean;
  isSTBMigrationOperation?: boolean;
  wrongAppAnalyticsUrl?: string;
  openTizenLegacyApp?: string;
  openTizenStore?: string;
  openWebOSLegacyApp?: string;
  openAndroidLegacyApp?: string;
  openGoogleStore?: string;
  openAppleLegacyApp?: string;
  openAppStore?: string;
  searchCharacteristics: {
    GROUP_BY_VALUE: string;
  };
  exponea: {
    enable: boolean;
    endpointsUiUrl?: string;
    authorization?: string;
    defaultDisplayTimeInSeconds?: number;
    requestTimeoutInSeconds?: number;
  };
  livePlaybackBufferFetching: number;
  gdpr: {
    cookiePolicyUrl?: string;
    useCustomCustomerCookiesPopup?: boolean;
    useExternalPopup?: boolean;
    externalPopupScriptUrl?: string;
  };
  promoScreen: {
    collectionId?: number;
    options: PromotionScreenOptions;
  };
  jwt: {
    isJWTSeamlessLoginEnabled?: boolean;
    jwtKeyName?: string;
    jwtRefreshTokenKeyName?: string;
  };
}

export type rcuSettingsRawType = {
  [key: string]: PREDEFINED_REFERENCES;
};

export type rcuSettingsType = {
  [key: string]: KuxMenuItem;
};

export let KuXDms: KuxDms;

export const kuxSetDMS = (dms: KuxDms) => {
  KuXDms = dms;
};

/**
 * Create KuxMenuItem for every rcuHotKey like 'greenButton' from dms.params.rcu_settings.
 * Example param dmsRcuSettings: {greenButton: 'homepage'}
 * @param dmsRcuSettings
 */
export const prepareRcuSettings = (dmsRcuSettings: rcuSettingsRawType): rcuSettingsType => {
  const rcuSettings: rcuSettingsType = {};

  Object.keys(dmsRcuSettings).forEach((key) => {
    const referenceId = dmsRcuSettings[key];

    const menuItem: KuxMenuItem = generateMenuItem({
      typeOfAppLink: TypeOfApplicationLink.page,
      inAppLink: referenceId,
      referenceId: referenceId,
      name: `${referenceId}_MenuItem`,
    });
    rcuSettings[key] = menuItem;
  });
  return rcuSettings;
};

function parseFilesFormat(filesFormat: { [type in FilesFormat]: string | string[] }): KuxDms['filesFormat'] {
  if (Array.isArray(Object.values(filesFormat)[0])) return filesFormat as KuxDms['filesFormat'];
  Object.keys(filesFormat).map((type) => {
    filesFormat[type as FilesFormat] = [filesFormat[type as FilesFormat] as string];
  });
  return filesFormat as KuxDms['filesFormat'];
}

/**
 * Get DMS per platform (temp solution - need to use kaltura-client)
 * @param parameters: DmsRequestParameters
 */
export function getDms(parameters: DmsRequestParameters): Observable<KuxDms> {
  const action = new ConfigurationsServeByDeviceAction({
    applicationName: parameters.applicationName,
    clientVersion: parameters.clientVersion,
    platform: parameters.platform,
    udid: parameters.udid,
    tag: parameters.tag,
    partnerId: parameters.partnerId,
  });
  const client = kalturaClient();
  return client.request(action, KalturaResponseType.json).pipe(
    map(parseDms),
    catchError((error: KuxError) => {
      throw new KuxError('DMS loading failed.', error?.code, error?.httpStatus);
    }),
  );
}

export function parseDms(result: any) {
  if (result?.result?.error && result?.result?.error?.code) {
    throw new KuxError(result?.result?.error?.message || 'DMS loading failed.', result?.result?.error?.code);
  }

  const { sentry, PlayerSettings } = result.params;
  let playerSettings = {} as DmsPlayerSettings;

  if (PlayerSettings) {
    playerSettings = {
      scriptId: PlayerSettings.SCRIPT_ID,
      uiConfId: PlayerSettings.uiconf_id,
      scriptUrl: PlayerSettings.SCRIPT_URL.replace('{uiconf_id}', PlayerSettings.uiconf_id),
      youbora: {
        accountCode: PlayerSettings.Youbora?.AccountCode,
      },
      mediaEntryCacheConfig: {
        allowMediaEntryCaching: PlayerSettings.MediaEntryCacheConfig?.allowMediaEntryCaching,
        maxMediaEntryCacheSize: PlayerSettings.MediaEntryCacheConfig?.maxMediaEntryCacheSize,
        timeoutMs: PlayerSettings.MediaEntryCacheConfig?.timeoutMs,
      },
      broadpeak: PlayerSettings.Broadpeak,
      warmupUrls: PlayerSettings.WarmupUrls,
      networkSettings: PlayerSettings.NetworkSettings,
      ottAnalytics: PlayerSettings.OttAnalytics,
      lowLatencyConfig: PlayerSettings.LowLatencyConfig,
      loadControlBuffers: PlayerSettings.loadControlBuffers,
      multicastLoadControlBuffers: PlayerSettings.multicastLoadControlBuffers,
      multicastSettings: PlayerSettings.multicastSettings,
      playerMulticastReloadTimeout: PlayerSettings.playerMulticastReloadTimeout,
    };
  }

  return {
    analytics: result.params.analytics,
    bgVideo: result.params.bg_video,
    CKEY: result.params?.CKEY || '',
    clientId: result.params?.clientId || '',
    ui: {
      defaultThrottleTimeout: result.params.ui?.DEFAULT_THROTTLE_TIMEOUT || 125,
      epgThrottleTimeout: result.params.ui?.EPG_THROTTLE_TIMEOUT || 125,
    },
    bingeWatching: {
      enableNextEpisode: !!result.params?.binge_watching?.enable_next_episode,
      enableBingeWatchingUi:
        result.params?.binge_watching && 'enable_binge_watching_ui' in result.params.binge_watching
          ? !!result.params.binge_watching.enable_binge_watching_ui
          : true,
      enableAutomaticNextEpisode: !!result.params?.binge_watching?.enable_automatic_next_episode,
      automaticNextEpisodeStartTime: Number(result.params?.binge_watching?.automatic_next_episode_start_time) || 7,
      nextEpisodeMaxCountdownTime: Number(result.params?.binge_watching?.next_episode_max_countdown_time) || 7,
      skipIntroMaxCountdownTime: Number(result.params?.binge_watching?.skip_intro_max_countdown_time) || 7,
      skipRecapMaxCountdownTime: Number(result.params?.binge_watching?.skip_recap_max_countdown_time) || 7,
    },
    bitrateLimitationManagement: {
      bitrateLimitSetBy: result.params.bitrate_limitation_management?.bitrate_limit_set_by,
      userAllowedTo: result.params.bitrate_limitation_management?.user_allowed_to || false,
      bitrateLimit: result.params.bitrate_limitation_management?.bitrate_limit,
      localVqSettings: result.params.bitrate_limitation_management?.local_vq_settings || {},
    },
    companyLogoUrl: result.params.loading_screen?.company_logo_url,
    consecutiveRequestFailuresLimit: result.params?.consecutive_request_failures_limit || 3,
    filesFormat: parseFilesFormat(result.params.FilesFormat),
    multicastParams: {
      HEVCCapable: result.params?.MulticastParams?.HEVC_CAPABLE,
      WVCASCapable: result.params?.MulticastParams?.WVCAS_CAPABLE,
      multicastEndpoint: result.params?.MulticastParams?.multicast_endpoint,
      multicastIndicationKey: result.params?.MulticastParams?.multicast_indication_key,
      multicastPauseResumeABRThreshold: result.params?.MulticastParams?.MULTICAST_PAUSE_RESUME_ABR_THRESHOLD,
    },
    endpointUrl: result.params.Gateways.RestGW,
    cloudFrontUrl: result.params.Gateways.CloudFrontGW,
    serverTimeOut: result.params.server_time_out || DEFAULT_SERVER_TIMEOUT, // defined in seconds in DMS, 10 sec by default
    forcedUpdateURLObject: result.params.ForcedUpdateURL,
    graphQlUrl: result.params.Gateways.GraphQLGW,
    isForceUpdate: result.params.isforceupdate,
    sentry,
    loadingAnimationUrl: result.params.loading_screen?.loading_animation_url,
    loadingBgColor: result.params.loading_screen?.loading_bg_color,
    mediaTypes: result.params.MediaTypes,
    mediaTypesValue: Object.keys(result.params.MediaTypes).reduce(
      (prev, cur) => ({ ...prev, [result.params.MediaTypes[cur]]: cur }),
      {},
    ),
    rootCategoryID: result.params.RootCategoryID,
    apiVersion: result.params.apiVersion,
    pingServerUrl: result.params.pingServerUrl,
    e2eRootCategoryID: result.params.e2eRootCategoryID,
    staticAssetsUrl: result.params.staticAssetsUrl,
    tutorialVideoAssetId: result.params.tutorial_video_url,
    suggestedRecalculationPeriod: result.params.Suggested_recalculation_period,
    suggestedCalculationByLastXItems: result.params.Suggested_calculation_by_last_x_items,
    upgradeWindowStartTime: result.params.upgrade_window_start_time,
    upgradeWindowEndTime: result.params.upgrade_window_end_time,
    savedInterestItemsList: result.params.personallist_enums?.saved_interest_items_list
      ? Number(result.params.personallist_enums?.saved_interest_items_list)
      : undefined,
    chosenSearchList: result.params.personallist_enums?.chosen_search_list
      ? Number(result.params.personallist_enums?.chosen_search_list)
      : undefined,
    personalList: result.params.personallist_enums?.personal_list
      ? Number(result.params.personallist_enums?.personal_list)
      : undefined,
    showCastAndCrewInSearch: result.params.show_cast_and_crew_in_search || false,
    // aggregate translation links
    translationLinks: Object.keys(result.params).reduce((prev, cur) => {
      if (cur.search('dictionary_url') === 0) {
        return { ...prev, [cur]: result.params[cur] };
      }
      return prev;
    }, {}),
    playerSettings,
    useCustomCustomerCookiesPopup: result.params?.GDPR?.useCustomCustomerCookiesPopup || false,
    o2Proxy: result.params?.o2Proxy,
    wrongPinTriesLimit: result.params.wrong_pin_tries_limit,
    wrongPinTimeout: result.params.wrong_pin_timeout,
    playableCatchupBuffer: result.params.playable_Catchup_Buffer ?? DEFAULT_PLAYABLE_CATCHUP_BUFFER,
    ratingMap: result.params.rating || [],
    ratingNameMap: arrayToMap(result.params.rating || [], 'name'),
    rcuSettings: prepareRcuSettings(result.params.rcu_settings || {}),
    launchDarkly: {
      sdk: result.params.launchDarkly?.sdk,
      mobile: result.params.launchDarkly?.mobile,
    },
    slowConnectionSamplingPeriod: result.params.slow_connection_sampling_period,
    deviceFamilyId: result.params?.deviceFamilyId,
    appInfoSerialType: result.params?.app_info_serial_type,
    temporaryParams: {
      endVoiceSearchTimeInSec: result.params?.temporary_params?.end_voice_search_time_in_sec || 0,
      subscriptionDenialList: result.params?.temporary_params?.purchase_subscription_device_brand_denial_list || [],
      ppvDenialList: result.params?.temporary_params?.purchase_ppv_device_brand_denial_list || [],
      morePlanCollection: result.params?.temporary_params?.more_plan_collection,
      filterLowerSubscription: result.params?.temporary_params?.filter_lower_subscription || true,
    },
    subscriptionIds: {
      ...(result.params?.SubscriptionIds?.Adults ? { adults: result.params?.SubscriptionIds?.Adults } : {}),
      ...(result.params?.SubscriptionIds?.FastForward
        ? { fastForward: result.params?.SubscriptionIds?.FastForward }
        : {}),
    },
    adultsConfigurations: result.params?.AdultsConfigurations,
    minFFEntitlementCheckIntervalSec: result.params?.fastForwardEntitlement?.MinFFEntitlementCheckIntervalSec || 600,
    mosaicInfo: {
      isMosaicSupported: result.params?.MosaicInfo?.isMosaicSupported || false,
      mosaicSplitViewEnabled: result.params?.MosaicInfo?.mosaic_split_view_enabled || false,
      mosaicPlayerRandomSecondsTime: result.params?.MosaicInfo?.MosaicPlayerRandomSecondsTime || 180,
    },
    silentLoginSettings: {
      loginTypeID: result.params?.silentLoginSettings?.LoginTypeID,
      pinType: result.params?.silentLoginSettings?.PINType,
      macType: result.params?.silentLoginSettings?.MACType,
      structure: result.params?.silentLoginSettings?.Structure,
      networkModeURL: result.params?.silentLoginSettings?.NetworkModeURL,
    },
    prePaidUserId: result.params?.prePaidUserId,
    providers: result.params?.providers ? prepareProviders(result.params.providers, normalizingProviderData) : {},
    epgTvTimelineInMinutes: result.params?.epg_tv_timeline_in_minutes || 210,
    GridCharacteristics: {
      GRID_TYPES: result.params?.GridCharacteristics?.GRID_TYPES || [],
      GROUP_BY_VALUE: result.params?.GridCharacteristics?.GROUP_BY_VALUE || 'name',
    },
    searchCharacteristics: {
      GROUP_BY_VALUE: result.params?.searchCharacteristics?.GROUP_BY_VALUE || '',
    },
    appLanguageCodes: result.params?.MigrationSection?.AppLanguageCodes,
    playerLanguageCodes: result.params?.MigrationSection?.PlayerLanguageCodes,
    isBGMigrationCheckNeeded: result.params?.MigrationSection?.isBGMigrationCheckNeeded,
    isSTBMigrationOperation: result.params?.MigrationSection?.isSTBMigrationOperation,
    wrongAppAnalyticsUrl: result.params?.MigrationSection?.wrongAppAnalyticsUrl,
    stbMigrationUrl: result.params?.MigrationSection?.stbMigrationUrl,
    openTizenLegacyApp: result.params?.MigrationSection?.openTizenLegacyApp,
    openTizenStore: result.params?.MigrationSection?.openTizenStore,
    openWebOSLegacyApp: result.params?.MigrationSection?.openWebOSLegacyApp,
    openAndroidLegacyApp: result.params?.MigrationSection?.openAndroidLegacyApp,
    openGoogleStore: result.params?.MigrationSection?.openGoogleStore,
    openAppleLegacyApp: result.params?.MigrationSection?.openAppleLegacyApp,
    openAppStore: result.params?.MigrationSection?.openAppStore,
    exponea: {
      enable: result.params?.exponea?.enable,
      endpointsUiUrl: result.params?.exponea?.endpointsUiUrl,
      authorization: result.params?.exponea?.authorization,
      defaultDisplayTimeInSeconds: result.params?.exponea?.defaultDisplayTimeInSeconds,
      requestTimeoutInSeconds: result.params?.exponea?.requestTimeoutInSeconds,
    },
    livePlaybackBufferFetching: (result.params?.livePlaybackBufferFetching || 5) * 60 * 1000,
    gdpr: {
      cookiePolicyUrl: result.params?.GDPR?.cookies_policy_url || '',
      useCustomCustomerCookiesPopup: result.params?.GDPR?.useCustomCustomerCookiesPopup,
      useExternalPopup: result.params?.GDPR?.useExternalPopup,
      externalPopupScriptUrl: result.params?.GDPR?.externalPopupScriptUrl,
    },
    promoScreen: {
      collectionId: result.params?.promotionScreenCollectionID,
      options: result.params?.PromotionScreen || {},
    },
    jwt: {
      isJWTSeamlessLoginEnabled: result.params?.jwt?.isJWTSeamlessLoginEnabled,
      jwtKeyName: result.params?.jwt?.JwtKeyName,
      jwtRefreshTokenKeyName: result.params?.jwt?.JwtRefreshTokenKeyName,
    },
  } as KuxDms;
}
